import dayjs from "dayjs";

export function calculateNormalDistribution(z, method = 3) {
  if (method === 1) {
    const GetZPercent = (z) => {
      //z == number of standard deviations from the mean

      //if z is greater than 6.5 standard deviations from the mean
      //the number of significant digits will be outside of a reasonable
      //range
      if (z < -6.5) return 0.0;
      if (z > 6.5) return 1.0;

      var factK = 1;
      var sum = 0;
      var term = 1;
      var k = 0;
      var loopStop = Math.exp(-23);
      while (Math.abs(term) > loopStop) {
        term =
          //Note: 1/(sqrt(2*pi)) === 0.3989422804
          (((0.3989422804 * Math.pow(-1, k) * Math.pow(z, k)) /
            (2 * k + 1) /
            Math.pow(2, k)) *
            Math.pow(z, k + 1)) /
          factK;
        sum += term;
        k++;
        factK *= k;
      }
      sum += 0.5;

      return sum;
    };
    return GetZPercent(z);
  }

  if (method === 2) {
    const T = 1 / (1 + 0.2316419 * Math.abs(z));
    const D = 0.3989423 * Math.exp((-z * z) / 2);
    const cumulativeDistribution =
      D *
      T *
      (0.3193815 +
        T * (-0.3565638 + T * (1.781478 + T * (-1.821256 + T * 1.330274))));
    if (z > 0) return 1 - cumulativeDistribution;
    return cumulativeDistribution;
  }
  if (method === 3) {
    function normDistZ(z) {
      const a1 = 0.31938153;
      const a2 = -0.356563782;
      const a3 = 1.781477937;
      const a4 = -1.821255978;
      const a5 = 1.330274429;

      const L = Math.abs(z);
      const K = 1 / (1 + 0.2316419 * L);
      const term =
        a1 * K +
        a2 * Math.pow(K, 2) +
        a3 * Math.pow(K, 3) +
        a4 * Math.pow(K, 4) +
        a5 * Math.pow(K, 5);
      const phi =
        1 - (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * L * L) * term;

      return z >= 0 ? phi : 1 - phi;
    }
    return normDistZ(z);
  }
}

export function calculateBlackScholesValue(overrideParameters = {}) {
  //Note: Math.log(x) === ln(x) in excel

  // if (isNaN(riskFreeRate) && !usingDefaultRiskFreeRate) {
  //   setUsingDefaultRiskFreeRate(true);
  //   riskFreeRate = blackScholesParameters.annualRiskFreeRate.value || 4.67;
  // }

  const strikePriceDividendAdjusted = false;

  const sigma = overrideParameters?.volatility / 100;
  const T = overrideParameters?.remainingTerm;
  const K = overrideParameters?.strikePrice;
  const S0 = overrideParameters?.sharePrice;
  // const S0Div = blackScholesParameters.dividendAdjustedCurrentStockPrice.value;
  const r = overrideParameters?.riskFreeRate / 100;
  const discountRate = overrideParameters?.dividendYield / 100;

  const S0Selected = S0;

  // Don't return values unless every value is typed in
  if (![sigma, T, K, S0, discountRate].every((value) => value || value === 0)) {
    return {};
  }

  const d1Numerator =
    Math.log(S0Selected / K) + (r - discountRate + Math.pow(sigma, 2) / 2) * T;
  const d1Denominator = sigma * Math.sqrt(T);
  const d1 = d1Numerator / d1Denominator;

  const d2 = d1 - sigma * Math.sqrt(T);

  const N_D1 = calculateNormalDistribution(d1);
  const N_D2 = calculateNormalDistribution(d2);

  const callPriceEstimate =
    S0Selected * Math.exp(-discountRate * T) * N_D1 -
    K * Math.exp(-r * T) * N_D2;
  // const putPriceEstimate =
  //   callPriceEstimate + (K * Math.exp(-r * T) - S0Selected);

  return { callPriceEstimate, nD1: N_D1 };
}

export function calculateTrancheTableValues(
  trancheTableValues,
  taxTableParams,
) {
  const shortTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Short-Term")?.totalTax || 0) /
    100;
  // To be used as table advances to detect short and long term captial gains
  const longTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Long-Term")?.totalTax || 0) /
    100;

  const remainingUnits = trancheTableValues?.initialUnits;
  const remainingFullValue = remainingUnits * trancheTableValues?.optionValue;
  const remainingFullValueAfterTax = Math.max(
    remainingFullValue * (1 - shortTermTaxRate),
    0,
  );

  const intrinsicValue = Math.max(
    remainingUnits *
      (trancheTableValues?.sharePrice - trancheTableValues?.strikePrice),
    0,
  );
  const intrinsicValueAfterTax = Math.max(
    intrinsicValue * (1 - shortTermTaxRate),
    0,
  );

  const killAmount = Math.max(remainingFullValue - intrinsicValue, 0);
  const killAmountAfterTax = Math.max(killAmount * (1 - shortTermTaxRate), 0);

  const killPercent = Math.max((100 * killAmount) / remainingFullValue, 0);

  const anticipatedAward = trancheTableValues?.anticipatedAward;

  return {
    remainingUnits: remainingUnits || "",
    remainingFullValue: remainingFullValue || "",
    remainingFullValueAfterTax: remainingFullValueAfterTax || "",
    intrinsicValue: intrinsicValue || "",
    intrinsicValueAfterTax: intrinsicValueAfterTax || "",
    killAmount: killAmount || "",
    killAmountAfterTax: killAmountAfterTax || "",
    killPercentage: killPercent || "",
    anticipatedAward: anticipatedAward || false,
  };
}

export function calculateTrancheTableRSUValues(
  trancheRowValues,
  taxTableParams,
  generalInfoParams,
) {
  const shortTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Short-Term")?.totalTax || 0) /
    100;
  // To be used as table advances to detect short and long term captial gains
  const longTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Long-Term")?.totalTax || 0) /
    100;

  const remainingUnits = trancheRowValues?.initialUnits;

  const dividendsForegone = 0;

  const remainingFullValue =
    remainingUnits * (trancheRowValues?.sharePrice || 0);

  // Old method - calculating tax on the value of the award
  // const remainingFullValueAfterTax = Math.max(
  //   remainingFullValue * (1 - shortTermTaxRate),
  //   0,
  // );

  const remainingUnitsAfterTax = Math.round(
    Number(remainingUnits) * (1 - shortTermTaxRate),
  );

  const remainingFullValueAfterTax =
    remainingUnitsAfterTax * (trancheRowValues?.sharePrice || 0);

  const exerciseDate =
    !trancheRowValues?.exerciseDate && trancheRowValues?.trancheDate
      ? dayjs(trancheRowValues?.trancheDate).add(1, "day").format()
      : trancheRowValues?.exerciseDate
        ? trancheRowValues?.exerciseDate
        : "";

  const updatedTrancheRowValues = {
    ...trancheRowValues,
    remainingUnits,
    remainingUnitsAfterTax,
    remainingFullValue,
    remainingFullValueAfterTax,
    exerciseDate,
    dividendsForegone,
  };
  return updatedTrancheRowValues;
}

export function calculateRemainingGrantOptionValues(
  optionTableValues,
  trancheTableValues,
  generalInfoParams,
  taxTableParams,
) {
  // if (!trancheTableValues?.length) {
  //   return {
  //     remainingGrantValue: "",
  //     remainingGrantValueAfterTax: "",
  //     remainingGrantIntrinsicValue: "",
  //     remainingGrantIntrinsicValueAfterTax: "",
  //     grantKillAmount: "",
  //     grantKillAmountAfterTax: "",
  //     grantKillPercentage: "",
  //     remainingGrantDelta: "",
  //     remainingGrantDeltaAfterTax: "",
  //     equationSolverValues: [],
  //   };
  // }
  const equationSolverValues = [];
  //TODO: After tax values may need to be a summation of the tranches
  // ...Not calculated here as some will be longterm some will be shortterm
  const shortTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Short-Term")?.totalTax || 0) /
    100;
  // To be used as table advances to detect short and long term captial gains
  const longTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Long-Term")?.totalTax || 0) /
    100;

  const remainingUnitsTotal = optionTableValues?.awardUnits || 0;
  // To use sum of tranche # of units instead of what user provides in option table:
  // const remainingUnitsTotal = trancheTableValues.reduce(
  //   (prev, curr) => prev + (parseInt(curr?.remainingUnits) || 0),
  //   0,
  // );

  // Value of all tranches' option units at option value price
  const remainingGrantValue =
    optionTableValues?.optionValue * remainingUnitsTotal;
  const remainingGrantValueAfterTax = Math.max(
    remainingGrantValue * (1 - shortTermTaxRate),
    0,
  );

  const remainingGrantIntrinsicValue = Math.max(
    remainingUnitsTotal *
      (generalInfoParams?.[0]?.sharePrice - optionTableValues?.strikePrice),
    0,
  );
  // To use sum of tranche values
  // const remainingGrantIntrinsicValue = trancheTableValues.reduce(
  //   (prev, curr) => prev + (parseFloat(curr?.intrinsicValue) || 0),
  //   0,
  // );

  const remainingGrantIntrinsicValueAfterTax = Math.max(
    remainingGrantIntrinsicValue * (1 - shortTermTaxRate),
    0,
  );

  const grantKillAmount = Math.max(
    remainingGrantValue - remainingGrantIntrinsicValue,
    0,
  );
  const grantKillAmountAfterTax = Math.max(
    grantKillAmount * (1 - shortTermTaxRate),
    0,
  );
  const grantKillPercentage = Math.max(
    (100 * grantKillAmount) / remainingGrantValue,
    0,
  );

  // Value of option for every $1 change in option price
  const remainingGrantDelta =
    (optionTableValues.nD1 / 100) * remainingUnitsTotal;
  const remainingGrantDeltaAfterTax = Math.max(
    remainingGrantDelta * (1 - shortTermTaxRate),
    0,
  );

  // Used to determine inflection point of totalValue - intrisicValue.
  // Consider making this it's own function separate from this function if it is slowing things down.
  for (
    let price = Math.floor(optionTableValues?.strikePrice) || 1;
    price < 200 + (Math.floor(optionTableValues?.strikePrice) || 1);
    price += 1
  ) {
    const newCalculatedBlackScholesValues = calculateBlackScholesValue({
      ...{ ...generalInfoParams?.[0], sharePrice: price },
      ...optionTableValues,
    });
    const currentPriceOptionValue =
      newCalculatedBlackScholesValues?.callPriceEstimate;

    const grantTotalValueAtPrice = currentPriceOptionValue;

    const intrinsicValueAtPrice = price - optionTableValues?.strikePrice;

    const grantKillAmountAtPrice =
      grantTotalValueAtPrice - intrinsicValueAtPrice;

    const grantKillPercentage =
      (100 * grantKillAmountAtPrice) / grantTotalValueAtPrice;

    const resultingValuesAtPrice = {
      price,
      grantTotalValueAtPrice,
      intrinsicValueAtPrice,
      grantKillAmountAtPrice,
      grantKillPercentage,
    };
    equationSolverValues.push(resultingValuesAtPrice);
  }

  return {
    remainingGrantValue:
      remainingGrantValue || remainingGrantValue === 0
        ? remainingGrantValue
        : "",
    remainingGrantValueAfterTax:
      remainingGrantValueAfterTax || remainingGrantValueAfterTax === 0
        ? remainingGrantValueAfterTax
        : "",
    remainingGrantIntrinsicValue:
      remainingGrantIntrinsicValue || remainingGrantIntrinsicValue === 0
        ? remainingGrantIntrinsicValue
        : "",
    remainingGrantIntrinsicValueAfterTax:
      remainingGrantIntrinsicValueAfterTax ||
      remainingGrantIntrinsicValueAfterTax === 0
        ? remainingGrantIntrinsicValueAfterTax
        : "",

    grantKillAmount:
      grantKillAmount || grantKillAmount === 0 ? grantKillAmount : "",
    grantKillAmountAfterTax:
      grantKillAmountAfterTax || grantKillAmountAfterTax === 0
        ? grantKillAmountAfterTax
        : "",
    grantKillPercentage:
      grantKillPercentage || grantKillPercentage === 0
        ? grantKillPercentage
        : "",
    remainingGrantDelta:
      remainingGrantDelta || remainingGrantDelta === 0
        ? remainingGrantDelta
        : "",
    remainingGrantDeltaAfterTax:
      remainingGrantDeltaAfterTax || remainingGrantDeltaAfterTax === 0
        ? remainingGrantDeltaAfterTax
        : "",
    equationSolverValues: equationSolverValues,
  };
}

export function calculateRemainingGrantRSUValues(
  RSUtableValues,
  generalInfoParams,
  trancheTableValues,
  taxTableParams,
) {
  if (!trancheTableValues?.length) {
    return {
      vestedAwardsValue: "",
      unvestedAwardsValue: "",
      totalAwardsValue: "",
      totalAwardsValueAfterTax: "",
      dividendRestrictedTotalValue: "",
      totalDividendsForegone: "",
      dividendAdjustedTotalValue: "",
      dividendAdjustedTotalValueAfterTax: "",
      remainingGrantDelta: "",
      remainingGrantDeltaAfterTax: "",
    };
  }
  //TODO: After tax values may need to be a summation of the tranches
  // ...Not calculated here as some will be longterm some will be shortterm
  const shortTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Short-Term")?.totalTax || 0) /
    100;
  // To be used as table advances to detect short and long term captial gains
  const longTermTaxRate =
    (taxTableParams.find((item) => item.ID === "Long-Term")?.totalTax || 0) /
    100;

  const totalAwards = RSUtableValues?.awardUnits;
  const remainingGrantDelta = totalAwards;
  const remainingGrantDeltaAfterTax = Math.max(
    totalAwards * (1 - shortTermTaxRate),
    0,
  );

  const vestedAwards = trancheTableValues.reduce(
    (prev, curr) =>
      prev +
      (!isNaN(parseInt(curr?.remainingUnits)) &&
      dayjs(generalInfoParams?.currentDate).isAfter(dayjs(curr?.exerciseDate))
        ? parseInt(curr?.remainingUnits)
        : 0),
    0,
  );
  const vestedAwardsValue = generalInfoParams?.sharePrice * vestedAwards;

  const unvestedAwards = trancheTableValues.reduce(
    (prev, curr) =>
      prev +
      (!isNaN(parseInt(curr?.remainingUnits)) &&
      dayjs(curr?.exerciseDate).isAfter(dayjs(generalInfoParams?.currentDate))
        ? parseInt(curr?.remainingUnits)
        : 0),
    0,
  );
  const unvestedAwardsValue = generalInfoParams?.sharePrice * unvestedAwards;
  const totalAwardsValue = generalInfoParams?.sharePrice * totalAwards;
  const totalAwardsValueAfterTax = Math.max(
    totalAwardsValue * (1 - shortTermTaxRate),
    0,
  );

  //Dividend-adjusted params
  // Use last Dividend tranche as the accumulated value:
  const totalDividendsForegone =
    trancheTableValues?.[trancheTableValues.length - 1]?.dividendsForegone
      ?.value || 0;
  // Or, if  you want a sum of dividends: (looks like double counting dividends to me though)
  // const totalDividendsForegone = trancheTableValues.reduce(
  //   (prev, curr) =>
  //     prev +
  //     (!isNaN(parseInt(curr?.dividendsForegone?.value))
  //       ? parseInt(curr?.dividendsForegone?.value)
  //       : 0),
  //   0,
  // );
  const dividendAdjustedTotalValue = totalAwardsValue - totalDividendsForegone;
  const dividendAdjustedTotalValueAfterTax = Math.max(
    dividendAdjustedTotalValue * (1 - shortTermTaxRate),
    0,
  );

  return {
    totalAwards: totalAwards || totalAwards === 0 ? totalAwards : "",
    vestedAwards: vestedAwards || vestedAwards === 0 ? vestedAwards : "",
    vestedAwardsValue:
      vestedAwardsValue || vestedAwardsValue === 0 ? vestedAwardsValue : "",
    unvestedAwards:
      unvestedAwards || unvestedAwards === 0 ? unvestedAwards : "",
    unvestedAwardsValue:
      unvestedAwardsValue || unvestedAwardsValue === 0
        ? unvestedAwardsValue
        : "",
    totalAwardsValue:
      totalAwardsValue || totalAwardsValue === 0 ? totalAwardsValue : "",
    totalAwardsValueAfterTax:
      totalAwardsValueAfterTax || totalAwardsValueAfterTax === 0
        ? totalAwardsValueAfterTax
        : "",
    totalDividendsForegone:
      totalDividendsForegone || totalDividendsForegone === 0
        ? totalDividendsForegone
        : "",
    dividendAdjustedTotalValue:
      dividendAdjustedTotalValue || dividendAdjustedTotalValue === 0
        ? dividendAdjustedTotalValue
        : "",
    dividendAdjustedTotalValueAfterTax:
      dividendAdjustedTotalValueAfterTax ||
      dividendAdjustedTotalValueAfterTax === 0
        ? dividendAdjustedTotalValueAfterTax
        : "",
    remainingGrantDelta:
      remainingGrantDelta || remainingGrantDelta === 0
        ? remainingGrantDelta
        : "",
    remainingGrantDeltaAfterTax:
      remainingGrantDeltaAfterTax || remainingGrantDeltaAfterTax === 0
        ? remainingGrantDeltaAfterTax
        : "",
  };
}

export const getUpdatedTermLengths = (optionAwardValues) => {
  const currentDate = dayjs(optionAwardValues.currentDate);
  const grantDate = dayjs(optionAwardValues.grantDate);
  const expiryDate = dayjs(optionAwardValues.expiryDate);

  const newTermLengths = { initialTerm: "", remainingTerm: "" };
  if (grantDate && expiryDate) {
    const initialTerm = expiryDate.diff(grantDate, "year", true);
    newTermLengths["initialTerm"] = initialTerm;
  }
  if (currentDate && expiryDate) {
    const remainingTerm = expiryDate.diff(currentDate, "year", true);
    newTermLengths["remainingTerm"] = remainingTerm;
  }
  return newTermLengths;
};

export const calculateRunTotals = (calculatedPlanValues) => {
  // Keys to aggregate
  const VALUE_KEYS = [
    // "remainingFullValue",
    // "dividendAdjustedTotalValueAfterTax",
  ];

  const CUSTOM_CALCS = [
    {
      label: "optionValue",
      // fn: (grant) => (grant.optionValue ?? 0) * (grant.remainingUnits ?? 0),
      fn: (grant) => grant.remainingFullValueAfterTax ?? 0,
      optionValueOnly: true,
    },
    {
      label: "rsuValue",
      // fn: (grant) => (grant.priceAtTranche ?? 0) * (grant.remainingUnits ?? 0),
      fn: (grant) =>
        (grant.remainingUnitsAfterTax > 0 ? grant.remainingUnitsAfterTax : 0) *
        (grant.priceAtTranche ?? 0),
      rsuValueOnly: true,
    },
  ];
  if (!calculatedPlanValues) return;
  return calculatedPlanValues.reduce((acc, run, i) => {
    const schedules = {
      options: run["Options Schedule"],
      rsu: run["RSU Schedule"],
    };

    const runResult = { run: i };

    for (const [label, schedule] of Object.entries(schedules)) {
      if (!schedule) {
        VALUE_KEYS.filter(
          (item) =>
            (!item.optionValueOnly && !item.rsuValueOnly) ||
            (item.optionValueOnly && label === "options") ||
            (item.rsuValueOnly && label === "rsu"),
        ).forEach((key) => {
          runResult[`${label}_${key}`] = 0;
        });

        CUSTOM_CALCS.filter(
          (item) =>
            (!item.optionValueOnly && !item.rsuValueOnly) ||
            (item.optionValueOnly && label === "options") ||
            (item.rsuValueOnly && label === "rsu"),
        ).forEach((calc) => {
          runResult[`${label}_${calc.label}`] = 0;
        });
        continue;
      }

      const grants = Object.values(schedule).flatMap((entries) =>
        Array.isArray(entries) ? entries : [entries],
      );

      VALUE_KEYS.filter(
        (item) =>
          (!item.optionValueOnly && !item.rsuValueOnly) ||
          (item.optionValueOnly && label === "options") ||
          (item.rsuValueOnly && label === "rsu"),
      ).forEach((key) => {
        runResult[`${label}_${key}`] = grants.reduce(
          (sum, grant) => sum + (grant[key] ?? 0),
          0,
        );
      });

      CUSTOM_CALCS.filter(
        (item) =>
          (!item.optionValueOnly && !item.rsuValueOnly) ||
          (item.optionValueOnly && label === "options") ||
          (item.rsuValueOnly && label === "rsu"),
      ).forEach((calc) => {
        runResult[`${label}_${calc.label}`] = grants.reduce(
          (sum, grant) => sum + calc.fn(grant),
          0,
        );
      });
    }

    acc.push(runResult);
    return acc;
  }, []);
};
